import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BatchMultipleContainersAlert from 'src/components/batch-multiple-containers-alert';
import Loader from 'src/components/loader';
import { useCurrentUserLocations, useCurrentUserRoles } from 'src/hooks/services/useUser';
import useActionLoadingStore from 'src/stores/useActionLoadingStore';
import { api } from 'src/utils/api';
import {
  API_RESOURCES,
  PERMANENT_CONTAINER_ACTIONS,
  USER_ROLES,
} from 'src/utils/constants';
import { getRouteURI, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

import routes from '../../../../utils/routes';
import PermanentContainerActionPageWrapper from './_action-wrapper';

const RelocatePermanentContainer = ({ user }) => {
  const { uuid: permanentContainerUUID } = useParams();
  const [permanentContainer, setPermanentContainer] = useState(undefined);
  const { isLoading, setLoading } = useActionLoadingStore();
  const [error, setError] = useState(null);
  const [batch, setBatch] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isLoadingSubLocations, setIsLoadingSubLocations] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [locationsState, setLocationsState] = useState({
    location: undefined,
    subLocation: undefined,
  });
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);

  const navigate = useNavigate();

  const { data: userRoles } = useCurrentUserRoles(user);
  const { data: userLocations } = useCurrentUserLocations(user);

  const getInitialData = async () => {
    try {
      setLoading(true);
      const getPermanentContainer = await api.get(`${API_RESOURCES.MATERIAL_CONTAINER}/${permanentContainerUUID}/`).json();
      const getLocations = await api.get(`${API_RESOURCES.LOCATION}/`).json();
      const [permanentContainer, { resources: locations }] = await Promise.all([getPermanentContainer, getLocations]);
      const { resources: subLocations } = await api.get(`${API_RESOURCES.SUB_LOCATION}/`, {
        searchParams: {
          'filter[location]': permanentContainer.location,
        },
      }).json();

      if (permanentContainer.current_batch) {
        const batch = await api.get(`${API_RESOURCES.MATERIAL_BATCH}/${getUuid(permanentContainer.current_batch)}/`).json();
        setBatch(batch);
      }

      setPermanentContainer(permanentContainer);
      setLocations(locations);

      if (!userRoles?.includes(USER_ROLES.MANAGER) && userLocations?.length > 0) {
        if (Array.isArray(userLocations) && Array.isArray(locations)) {
          setLocations(locations.filter(location => userLocations.includes(location.uri)));
        }
      }

      setSubLocations(subLocations);
      setLocationsState({
        location: permanentContainer?.location,
        subLocation: permanentContainer?.sub_location,
      });

    } catch (error) {
      if (error.name === 'HTTPError' && error.response.status === 404) {
        setError('Failed to find batch.');
      } else {
        setError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => void getInitialData(), [userLocations, userRoles]);

  const onRelocatePermanentContainer = async (permanentContainerUri, locationUri, subLocationUri) => {
    const subLocation = subLocations.find((subLocation) => subLocation.uri === locationsState.subLocation)?.name;
    const location = locations.find((location) => location.uri === locationsState.location)?.name;

    try {
      /* eslint-disable camelcase */
      const payload = {
        metadata: {
          destination_location: locationUri,
          destination_sub_location: subLocationUri,
        },
        source_batch: batch?.uri,
        action_type: PERMANENT_CONTAINER_ACTIONS.RELOCATE,
        source_material_container: permanentContainerUri,
      };

      setSubmitting(true);
      await api.post('material-container-action/', {
        json: payload,
      }).json();
    } catch (error_) {
      console.error(error_);
      setSubmitError(true);
      setSubmitting(false);
      return;
    }


    navigate(getRouteURI(routes.permanentContainerSuccess,
      { uuid: permanentContainerUUID },
      {
        action: PERMANENT_CONTAINER_ACTIONS.RELOCATE,
        permanentContainer: permanentContainer.uri,
        subLocation,
        location,
      }));

    return new Promise(() => {
    });
  };

  async function fetchSubLocations(locationUri) {
    try {
      setIsLoadingSubLocations(true);
      const { resources: subLocations } = await api.get(`${API_RESOURCES.SUB_LOCATION}/`, {
        searchParams: {
          'filter[location]': locationUri,
        },
      }).json();
      setSubLocations(subLocations);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingSubLocations(false);
    }
  }

  const handleChangeLocation = async (locationUri) => {
    await fetchSubLocations(locationUri);

    setLocationsState((previous) => ({
      ...previous,
      location: locationUri,
      subLocation: undefined,
    }));
  };

  const handleChangeSubLocation = async (subLocationUri) => {
    setLocationsState((previous) => ({
      ...previous,
      subLocation: subLocationUri,
    }));
  };

  return (
    <PermanentContainerActionPageWrapper
      id={permanentContainerUUID}
      user={user}
      httpError={error}
      customErrorText={submitError}
      action={PERMANENT_CONTAINER_ACTIONS.RELOCATE}
      isLoading={isLoading}
    >
      <h2 className="header-margin d-flex flex-column gap-5 fs-mobile-20">
        Relocate Permanent Container
        <span className="badge badge-secondary fs-mobile-18 p-1">
          {permanentContainer?.name}
        </span>
      </h2>

      <h5 className="header-margin fs-mobile-14 text-justify-custom">Select a Location and Sub-Location to store this permanent container.</h5>

      <div>
        <select
          className="form-control mt-4 mb-4"
          value={locationsState.location}
          onChange={(event) => handleChangeLocation(event.target.value)}
        >
          {locations.map((location) => (
            <option key={location?.uri} value={location?.uri}>{location?.name}</option>
          ))}
        </select>


        {isLoadingSubLocations ?
          <Loader inline className="mb-4" />
          : (
            <select
              className="form-control mb-4"
              value={locationsState.subLocation}
              disabled={!subLocations.length || isLoadingSubLocations}
              onChange={(event) => handleChangeSubLocation(event.target.value)}
            >
              <option value="">Choose...</option>
              {subLocations.map((subLocation) => (
                <option key={subLocation?.uri} value={subLocation?.uri}>{subLocation?.name}</option>
              ))}
            </select>
          )}

      </div>


      <button
        type="submit"
        className="btn btn-lg btn-primary btn-block"
        disabled={isLoadingSubLocations || isSubmitting || !locationsState.subLocation || !subLocations.length}
        onClick={() => onRelocatePermanentContainer(permanentContainer?.uri, locationsState.location, locationsState.subLocation)}
      >
        <div className="d-flex align-items-center justify-content-center fs-mobile-18">
          Confirm
          {isSubmitting && <Loader inline className="spacer-left" showText={false} />}
        </div>

      </button>
      <Link to={getRouteURI(routes.permanentContainerDetails,
        { uuid: permanentContainerUUID })}
      >
        <button type="button" disabled={isSubmitting} className="btn btn-default btn-action fs-mobile-18">
          Cancel
        </button>
      </Link>

      <BatchMultipleContainersAlert sourceBatch={batch} sourceContainer={permanentContainer} />

    </PermanentContainerActionPageWrapper>
  );
};

RelocatePermanentContainer.propTypes = {
  user: userPropType,
};

export default RelocatePermanentContainer;
